const productSectionCache = new Map();
const productSectionWarmCache = new Map();
const MAX_CACHED_PRODUCT_SECTIONS = 18;

class LinkedProductPicker extends HTMLElement {
  connectedCallback() {
    this.currentVariants = this.parseJSON(this.dataset.currentVariants);
    this.root = this.closest('product-information') || this.closest('[data-section-id]') || document;
    this.updateLinks();

    this.handleOptionChange = this.handleOptionChange.bind(this);
    this.handleClick = this.handleClick.bind(this);
    this.handlePrefetch = this.handlePrefetch.bind(this);

    this.root.addEventListener('change', this.handleOptionChange);
    this.addEventListener('click', this.handleClick);
    this.addEventListener('pointerenter', this.handlePrefetch, true);
    this.addEventListener('pointerdown', this.handlePrefetch, true);
    this.addEventListener('focusin', this.handlePrefetch);
  }

  disconnectedCallback() {
    this.root?.removeEventListener('change', this.handleOptionChange);
    this.removeEventListener('click', this.handleClick);
    this.removeEventListener('pointerenter', this.handlePrefetch, true);
    this.removeEventListener('pointerdown', this.handlePrefetch, true);
    this.removeEventListener('focusin', this.handlePrefetch);
  }

  parseJSON(value) {
    try {
      return JSON.parse(value || '[]');
    } catch (_error) {
      return [];
    }
  }

  selectedVariantId() {
    const formVariantInput = this.root.querySelector('form[action*="/cart/add"] input[name="id"]');
    return formVariantInput ? String(formVariantInput.value) : null;
  }

  selectedModel() {
    const selectedInput = Array.from(this.root.querySelectorAll('select, input[type="radio"]:checked')).find((input) => {
      const name = String(input.dataset.optionName || input.name || '').toLowerCase();
      return name.includes('device') || name.includes('phone model') || name.includes('model');
    });
    if (selectedInput) return selectedInput.value;
    const selectedId = this.selectedVariantId();
    const selectedVariant = this.currentVariants.find((variant) => String(variant.id) === selectedId);
    return selectedVariant ? selectedVariant.model : null;
  }

  selectedMagSafe() {
    const selectedInput = Array.from(this.root.querySelectorAll('input[type="radio"]:checked')).find(
      (input) => String(input.dataset.optionName || '').toLowerCase().includes('magsafe')
    );
    if (selectedInput) return selectedInput.value;
    const selectedId = this.selectedVariantId();
    return this.currentVariants.find((variant) => String(variant.id) === selectedId)?.magsafe ?? null;
  }

  handleOptionChange(event) {
    const target = event.target;

    if (!(target instanceof HTMLSelectElement || target instanceof HTMLInputElement)) {
      window.setTimeout(() => {
        this.updateLinks();
      }, 0);
      return;
    }

    const selectedOption = target instanceof HTMLSelectElement ? target.options[target.selectedIndex] : target;
    const optionName = String(
      selectedOption?.dataset.optionName || target.dataset.optionName || target.name || ''
    ).toLowerCase();

    if (optionName.includes('device') || optionName.includes('phone model') || optionName.includes('model')) {
      this.updateLinks(target.value);
      return;
    }

    window.setTimeout(() => {
      this.updateLinks();
    }, 0);
  }

  handlePrefetch(event) {
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (connection?.saveData || String(connection?.effectiveType || '').includes('2g')) return;
    if (event.type === 'pointerenter' && event.pointerType !== 'mouse') return;
    const option = event.target.closest?.('[data-base-url]');
    if (!(option instanceof HTMLAnchorElement) || option.classList.contains('is-selected')) return;

    this.preloadLinkedImage(option, 'high');

    this.warmProductSection(option.href, 'high').catch(() => {
      productSectionCache.delete(this.sectionCacheKey(option.href));
      productSectionWarmCache.delete(this.sectionCacheKey(option.href));
    });
  }

  showOptimisticImage(option) {
    const source = option.dataset.prefetchImage;
    const primaryImage = document.querySelector('.product-information__media .product-media__image');
    if (!source || !(primaryImage instanceof HTMLImageElement)) return;

    primaryImage.removeAttribute('srcset');
    primaryImage.removeAttribute('sizes');
    primaryImage.fetchPriority = 'high';
    primaryImage.src = source;
    primaryImage.dataset.linkedProductOptimistic = 'true';
  }

  async handleClick(event) {
    const option = event.target.closest?.('[data-base-url]');
    if (!(option instanceof HTMLAnchorElement) || option.classList.contains('is-selected')) return;
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    event.preventDefault();
    this.updateLinks();
    const destination = option.href;
    this.showOptimisticImage(option);

    const main = document.querySelector('main');
    const primarySection = this.closest('.shopify-section');
    if (!main || !primarySection?.id) {
      window.location.assign(destination);
      return;
    }

    const primarySectionId = primarySection.id;
    primarySection.inert = true;
    primarySection.setAttribute('aria-busy', 'true');

    this.querySelectorAll('.is-loading').forEach((item) => item.classList.remove('is-loading'));
    option.classList.add('is-loading');
    this.setAttribute('aria-busy', 'true');
    main.classList.add('linked-product-navigation--loading');

    try {
      const currentFeatureSection = document.querySelector(
        '[id^="shopify-section-"][id*="__product_feature_stories"]'
      );
      const featureSectionId = currentFeatureSection?.id?.replace(/^shopify-section-/, '');
      const nextFeatureSectionPromise = featureSectionId
        ? this.fetchSectionById(destination, featureSectionId)
        : Promise.resolve(null);

      const [nextPrimarySection, nextFeatureSection] = await Promise.all([
        this.warmProductSection(destination),
        nextFeatureSectionPromise,
      ]);
      if (!nextPrimarySection) throw new Error('Product information section was not found');

      const importedPrimarySection = document.importNode(nextPrimarySection, true);
      const importedFeatureSection = nextFeatureSection
        ? document.importNode(nextFeatureSection, true)
        : null;

      history.replaceState({}, '', destination);
      const nextHeading = importedPrimarySection.querySelector('h1')?.textContent?.trim();
      if (nextHeading) document.title = `${nextHeading} – Vyrloom`;

      const currentCanonical = document.querySelector('link[rel="canonical"]');
      if (currentCanonical) {
        const canonicalUrl = new URL(destination, window.location.origin);
        canonicalUrl.search = '';
        currentCanonical.href = canonicalUrl.href;
      }

      primarySection.replaceWith(importedPrimarySection);
      customElements.upgrade(importedPrimarySection);

      if (currentFeatureSection && importedFeatureSection) {
        currentFeatureSection.replaceWith(importedFeatureSection);
        customElements.upgrade(importedFeatureSection);
        this.hydrateDeferredStoryImages(importedFeatureSection);
      }
    } catch (error) {
      console.error('Linked product navigation failed', error);
      window.location.assign(destination);
    } finally {
      primarySection.inert = false;
      primarySection.removeAttribute('aria-busy');
      main.classList.remove('linked-product-navigation--loading');
      this.removeAttribute('aria-busy');
    }
  }

  preloadLinkedImage(option, priority = 'low') {
    const source = option.dataset.prefetchImage;
    if (!source) return Promise.resolve();

    this.linkedImagePrefetches ||= new Map();
    if (!this.linkedImagePrefetches.has(source)) {
      const image = new Image();
      image.fetchPriority = priority;
      image.src = source;

      const ready = typeof image.decode === 'function'
        ? image.decode().catch(() => {})
        : new Promise((resolve) => {
            image.addEventListener('load', resolve, { once: true });
            image.addEventListener('error', resolve, { once: true });
          });

      this.linkedImagePrefetches.set(source, ready);
    }

    return this.linkedImagePrefetches.get(source);
  }

  async preloadPrimaryImage(section) {
    const image = section.querySelector('.product-information__media img');
    const source = image?.getAttribute('src');
    if (!source) return;

    const preload = new Image();
    const srcset = image.getAttribute('srcset');
    const sizes = image.getAttribute('sizes');
    if (srcset) preload.srcset = srcset;
    if (sizes) preload.sizes = sizes;
    preload.src = source;

    const ready = typeof preload.decode === 'function'
      ? preload.decode().catch(() => {})
      : new Promise((resolve) => {
          preload.addEventListener('load', resolve, { once: true });
          preload.addEventListener('error', resolve, { once: true });
        });

    await Promise.race([
      ready,
      new Promise((resolve) => window.setTimeout(resolve, 800)),
    ]);
  }

  async fetchSectionById(url, sectionId) {
    const requestUrl = new URL(url, window.location.origin);
    requestUrl.searchParams.set('section_id', sectionId);

    const response = await fetch(requestUrl.href, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      priority: 'high',
    });
    if (!response.ok) throw new Error(`Section request failed with ${response.status}`);

    const html = await response.text();
    const newDocument = new DOMParser().parseFromString(html, 'text/html');
    return newDocument.getElementById(`shopify-section-${sectionId}`);
  }

  hydrateDeferredStoryImages(section) {
    section.querySelectorAll('img[data-vyrloom-deferred-story-image]').forEach((image) => {
      if (!(image instanceof HTMLImageElement)) return;

      const srcset = image.dataset.srcset;
      const src = image.dataset.src;
      if (srcset) image.srcset = srcset;
      if (src) image.src = src;
      image.removeAttribute('data-srcset');
      image.removeAttribute('data-src');
      image.removeAttribute('data-vyrloom-deferred-story-image');
    });
  }

  sectionId() {
    return this.closest('.shopify-section')?.id?.replace(/^shopify-section-/, '') || '';
  }

  sectionCacheKey(url) {
    return `${this.sectionId()}::${url}`;
  }

  fetchProductSection(url, priority = 'high') {
    const sectionId = this.sectionId();
    if (!sectionId) return Promise.reject(new Error('Product section ID was not found'));

    const key = this.sectionCacheKey(url);
    if (!productSectionCache.has(key)) {
      const requestUrl = new URL(url, window.location.origin);
      requestUrl.searchParams.set('section_id', sectionId);

      const request = fetch(requestUrl.href, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        priority,
      })
        .then((response) => {
          if (!response.ok) throw new Error(`Product request failed with ${response.status}`);
          return response.text();
        })
        .then((html) => {
          const newDocument = new DOMParser().parseFromString(html, 'text/html');
          const section = newDocument.getElementById(`shopify-section-${sectionId}`);
          if (!section) throw new Error('Product section response was invalid');
          return section;
        });

      productSectionCache.set(key, request);

      if (productSectionCache.size > MAX_CACHED_PRODUCT_SECTIONS) {
        const oldestKey = productSectionCache.keys().next().value;
        if (oldestKey) productSectionCache.delete(oldestKey);
      }
    }

    return productSectionCache.get(key);
  }

  warmProductSection(url, priority = 'high') {
    const key = this.sectionCacheKey(url);
    if (!productSectionWarmCache.has(key)) {
      const request = this.fetchProductSection(url, priority)
        .then(async (section) => {
          await this.preloadPrimaryImage(section);
          return section;
        })
        .catch((error) => {
          productSectionWarmCache.delete(key);
          throw error;
        });

      productSectionWarmCache.set(key, request);
    }

    return productSectionWarmCache.get(key);
  }

  updateLinks(selectedModel = this.selectedModel()) {
    const selectedMagSafe = this.selectedMagSafe();
    this.querySelectorAll('[data-base-url]').forEach((option) => {
      const variants = this.parseJSON(option.dataset.variants);
      const matchingVariant = selectedModel === null ? null : variants.find((variant) =>
        variant.model === selectedModel && (selectedMagSafe === null || variant.magsafe == null || variant.magsafe === selectedMagSafe)
      );

      option.href = matchingVariant ? `${option.dataset.baseUrl}?variant=${matchingVariant.id}` : option.dataset.baseUrl;

      const unavailable = matchingVariant ? !matchingVariant.available : !variants.some((variant) => variant.available);
      option.classList.toggle('is-unavailable', unavailable);

      const baseLabel = option.getAttribute('aria-label').replace(/\s+—\s+Sold out$/, '');
      option.setAttribute('aria-label', unavailable ? `${baseLabel} — Sold out` : baseLabel);
    });
  }
}

if (!customElements.get('linked-product-picker')) {
  customElements.define('linked-product-picker', LinkedProductPicker);
}


